import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Box, Button, IconButton } from '@mui/material';
import {
  Close,
  KeyboardArrowRight,
  PauseRounded,
  PlayArrowRounded,
} from '@mui/icons-material';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { useHistory } from 'react-router-dom';
import Tooltip from '@src/components/Tooltip';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { COLOR, TRANSPARENT_COLOR } from '@src/styles/color';
import sanitizeHtml from 'sanitize-html';
import Typography from '@src/new-components/Typography';
import {
  SUB_STATUS,
  VOICE_CLONE_TYPE,
  VOICE_COMPLETED,
  VOICE_STATUS,
  STEP_VOICE_DETAIL,
} from '@src/constants/voiceCloning';
import { LANDING_PAGE_URL } from '@src/configs';
import CommonButton from '@src/new-components/Button';
import apis from '@src/apis';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { StyledFrameImg } from '@src/new-components/Avatar/index.style';
import { getFrameVoiceUrl } from '@src/services/voiceCloning';
import {
  StyledActionButtons,
  StyledDialogNote,
  StyledDialogTitle,
  StyledInfoDetail,
  StyledNewAudioSlider,
  StyledNewDialogVoiceDetailDesktop,
  StyledTag,
  StyledTags,
  StyledVoiceDetail,
} from './index.style';
import StatusDetailPublishVoice, {
  StatusDetail,
} from './StatusDetailPublishVoice';
import InfoTooltipStatusVoice from './InfoTooltipStatusVoice';
import FormShareAndEarn from './FormShareAndEarn';
import NewEditVoice from './NewEditVoice';

const Title = ({ title, handleCloseDialog }) => (
  <StyledDialogTitle>
    <Typography
      component="p"
      variant="body-20-bold"
      color={COLOR.descriptionGray}
    >
      {title}
    </Typography>
    <IconButton className="close-icon" onClick={handleCloseDialog}>
      <Close />
    </IconButton>
  </StyledDialogTitle>
);

const Avatar = ({ avatar, name, status, frameImageUrl }) => (
  <Box className="avatar-wrapper">
    <div style={{ position: 'relative' }}>
      <img src={avatar} alt="avatar" className="avatar" />
      {frameImageUrl && (
        <StyledFrameImg
          src={frameImageUrl}
          alt="badge"
          width={80}
          height={80}
          transform="scale(1.5)"
          top="54%"
          left="1px"
        />
      )}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Typography
        component="p"
        variant="body-16-bold"
        color={COLOR.descriptionGray}
      >
        {name}
      </Typography>
      <InfoTooltipStatusVoice status={status} />
    </div>
  </Box>
);

const VoiceDetail = ({
  open,
  onClose,
  handleChangeVoiceData,
  setWarningTitle,
  setWarningTextConfirm,
  setOpenWarningDialog,
  setWarningTextConfirmValue,
  setIsShowWarningContent,
  stepVoiceDetail,
  setStepVoiceDetail,
}) => {
  const { t } = useTranslation();
  const { voice } = useSelector((state) => state.voiceVoiceCloning);
  const dispatch = useDispatch();
  const history = useHistory();
  const audioRef = useRef(null);

  const showBtnShare = voice?.status === VOICE_STATUS.PUBLIC;

  const [voiceCloningInTTS, setVoiceCloningInTTS] = useState(null);
  const isTraining = voice?.status === VOICE_STATUS.TRAINING;
  const isTrained = voice?.status === VOICE_STATUS.TRAINED;
  const [voiceInfo, setVoiceInfo] = useState(voice);
  const [audioLink, setAudioLink] = useState('');
  const [playAudio, setPlayAudio] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  const [disabledInteract, setDisabledInteract] = useState(false);
  const [isDisabledDelete, setIsDisabledDelete] = useState(false);
  const [isEditVoiceInfo, setIsEditVoiceInfo] = useState(false);
  const [isLoadingCancelRequest, setIsLoadingCancelRequest] = useState(false);
  const [showPopupUnpublishVoice, setShowPopupUnpublishVoice] = useState(false);
  const [isLoadingUnpublishVoice, setIsLoadingUnpublishVoice] = useState(false);
  const [isLoadingCancelUnpublishVoice, setIsLoadingCancelUnpublishVoice] =
    useState(false);

  const handleUseVoiceNow = () => {
    dispatch(actions.synthesisRequest.updateVoice(voiceInfo));
    history.push(route.TTS);
  };

  const handleRedirectVoiceProfile = () => {
    window.open(`${LANDING_PAGE_URL}/voices/${voiceInfo?.code}`, '_blank');
  };

  const handleDeleteVoice = async () => {
    onClose();
    setWarningTitle(t('confirmDeleteVoice'));
    setWarningTextConfirm('confirmDeleteVoiceText');
    setWarningTextConfirmValue(voice);
    setIsShowWarningContent(false);
    setOpenWarningDialog(true);
  };

  const handleTogglePlayAudio = () => {
    audioRef.current.load();
    if (playAudio) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlayAudio(!playAudio);
  };

  const handleSliderChange = (event, newValue) => {
    const audio = audioRef.current;
    const newTime = (newValue / 100) * audio.duration;
    audio.currentTime = newTime;
    setSliderValue(newValue);
  };

  const onAudioEnded = () => {
    setPlayAudio(false);
    setSliderValue(0);
  };

  const handleCloseDialog = () => {
    onClose();
    setPlayAudio(false);
    setSliderValue(0);
    setIsEditVoiceInfo(false);
  };

  const handleAudioTimeUpdate = () => {
    const time = audioRef.current.currentTime;
    setSliderValue((time / audioRef.current.duration) * 100);
  };

  const updateVoice = (newVoice) => {
    handleChangeVoiceData(newVoice.id, newVoice);
    setVoiceInfo(newVoice);
  };

  const goBack = () => setIsEditVoiceInfo(false);
  useEffect(() => {
    setVoiceInfo(voice);
    setDisabledInteract(!VOICE_COMPLETED.includes(voice?.status));

    const isInstantVoice = voice?.type === VOICE_CLONE_TYPE.ZERO_SHOT;
    if (isInstantVoice) {
      setIsDisabledDelete(false);
    } else {
      const isStatusVoiceCanDelete = [
        VOICE_STATUS.RECORDING,
        VOICE_STATUS.REJECTED,
        VOICE_STATUS.READY,
        VOICE_STATUS.USING,
        VOICE_STATUS.PRIVATE,
        VOICE_STATUS.FAILED,
        VOICE_STATUS.SUSPENDED,
      ].includes(voice?.status);

      const isWaitingForPublicVoice =
        voice?.status === VOICE_STATUS.PRIVATE &&
        voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL;

      setIsDisabledDelete(!isStatusVoiceCanDelete || isWaitingForPublicVoice);
    }
  }, [voice]);

  useEffect(() => {
    const audio = audioRef.current;

    if (audio) audio.addEventListener('ended', onAudioEnded);

    return () => {
      if (audio) audio.removeEventListener('ended', onAudioEnded);
    };
  }, []);

  useEffect(() => {
    const audioDemo =
      voiceCloningInTTS?.demo || voice?.sampleVoices?.[0]?.sampleLink;
    if (audioDemo) setAudioLink(audioDemo);
  }, [voice?.sampleVoices, voiceCloningInTTS?.demo]);

  const fetchVoiceCloningInTTS = useCallback(async () => {
    if (!voice?.code) return;
    const response = await apis.voices.getVoiceCloningInTTS(voice.code);
    setVoiceCloningInTTS(response?.result?.voice);
  }, [voice?.code]);

  useEffect(() => {
    fetchVoiceCloningInTTS();
  }, [fetchVoiceCloningInTTS]);

  const handleCancelPublishVoice = async () => {
    setIsLoadingCancelRequest(true);
    const response = await apis.voiceCloning.cancelPublishVoice(voiceInfo?.id);
    const isSuccess = response.status;
    if (isSuccess) {
      setIsLoadingCancelRequest(false);
      dispatch(
        actions.noti.push({
          message: 'cancelRequestSuccess',
          severity: 'success',
        }),
      );
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(true));
      onClose();
      return;
    }

    setIsLoadingCancelRequest(false);
    dispatch(
      actions.noti.push({
        message: 'cancelRequestFailed',
        severity: 'error',
      }),
    );
  };

  const handleUnpublishVoice = async () => {
    setIsLoadingUnpublishVoice(true);
    const response = await apis.voiceCloning.unpublishVoice(voiceInfo?.id);
    const isSuccess = response?.status;
    if (isSuccess) {
      setIsLoadingUnpublishVoice(false);
      dispatch(
        actions.noti.push({
          message: 'unpublishVoiceSuccess',
          severity: 'success',
        }),
      );
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(true));
      onClose();
      return;
    }

    setIsLoadingUnpublishVoice(false);
    dispatch(
      actions.noti.push({
        message: 'unpublishVoiceFailed',
        severity: 'error',
      }),
    );
  };

  const handleCancelUnpublishVoice = async () => {
    setIsLoadingCancelUnpublishVoice(true);
    const response = await apis.voiceCloning.cancelUnpublishVoice(
      voiceInfo?.id,
    );
    const isSuccess = response?.status;
    if (isSuccess) {
      setIsLoadingCancelUnpublishVoice(false);
      dispatch(
        actions.noti.push({
          message: 'cancelUnpublishVoiceSuccess',
          severity: 'success',
        }),
      );
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(true));
      onClose();
      return;
    }

    setIsLoadingCancelUnpublishVoice(false);
    dispatch(
      actions.noti.push({
        message: 'cancelUnpublishVoiceFailed',
        severity: 'error',
      }),
    );
  };

  const AudioSample = () => (
    <Box className="audio-sample-wrapper">
      <IconButton
        className={`button-play ${!audioLink ? 'disabled' : ''}`}
        onClick={handleTogglePlayAudio}
        disabled={!audioLink}
      >
        {playAudio ? <PauseRounded /> : <PlayArrowRounded />}
      </IconButton>
      <StyledNewAudioSlider
        value={sliderValue}
        step={1}
        min={0}
        max={100}
        onChange={handleSliderChange}
      />
    </Box>
  );

  const ActionButtons = () => {
    const isPrivateVoice = voiceInfo?.status === VOICE_STATUS.PRIVATE;
    const isPublicVoice = voiceInfo?.status === VOICE_STATUS.PUBLIC;
    return (
      <StyledActionButtons>
        <Tooltip
          arrow
          title={t('descriptionDeleteVoice')}
          placement="top"
          disableHoverListener={!isDisabledDelete || isTraining || isTrained}
        >
          <span style={{ width: '32%' }}>
            <CommonButton
              variant="outlined"
              disabled={isDisabledDelete}
              onClick={handleDeleteVoice}
              startIcon={
                <DeleteRoundedIcon
                  sx={{
                    fontSize: '24px !important',
                    color: COLOR.errorDropZone,
                  }}
                />
              }
              textProps={{
                variantText: 'body-14-medium',
                color: COLOR.indigo.default,
              }}
              customStyle={{
                width: '100%',
              }}
            >
              {t('deleteVoice')}
            </CommonButton>
          </span>
        </Tooltip>
        <CommonButton
          variant="outlined"
          onClick={handleUseVoiceNow}
          textProps={{
            variantText: 'body-16-bold',
            color: COLOR.indigo.default,
          }}
          customStyle={{
            width: '70%',
          }}
          disabled={!isPrivateVoice && !isPublicVoice}
        >
          {t('useNow')}
        </CommonButton>
      </StyledActionButtons>
    );
  };

  const DescriptionVoice = () => {
    if (!voiceInfo?.description) return null;

    return (
      <Typography
        component="p"
        variant="body-14-regular"
        color={COLOR.descriptionGray}
        style={{
          wordBreak: 'break-word',
        }}
      >
        {voiceInfo?.description || ''}
      </Typography>
    );
  };

  const TagsVoice = () => {
    const keyRetentionDays =
      voiceInfo.retentionDays === 365 ? 'yearNumber' : 'dayNumber';

    const tags = [
      voiceInfo?.category?.name ? t(voiceInfo.category.name) : null,
      voiceInfo?.gender ? t(voiceInfo.gender) : null,
      voiceInfo?.province?.name || null,
      voiceInfo?.locale ? `${t('voiceLocale')} ${t(voiceInfo.locale)}` : null,
      voiceInfo?.ageGroup ? t(voiceInfo.ageGroup) : null,
      voiceInfo?.retentionDays
        ? t(keyRetentionDays, {
            value: 1,
            days: voiceInfo.retentionDays,
          })
        : null,
    ].filter(Boolean);

    return (
      <StyledTags>
        {tags.map((tag) => (
          <StyledTag key={tag}>
            <Typography variant="body-14-regular" color={COLOR.descriptionGray}>
              {tag}
            </Typography>
          </StyledTag>
        ))}
      </StyledTags>
    );
  };

  const InfoVoice = () => (
    <NewEditVoice
      voiceCloningInTTS={voiceCloningInTTS}
      updateVoice={updateVoice}
      goBack={goBack}
      refreshVoiceCloningInTTS={fetchVoiceCloningInTTS}
    />
  );

  const DetailVoice = () => (
    <>
      {!isEditVoiceInfo ? (
        <StyledVoiceDetail
          disabledInteract={disabledInteract}
          isDisabledDelete={isDisabledDelete}
        >
          <Title
            title={t('voiceClone')}
            handleCloseDialog={handleCloseDialog}
          />
          <Avatar
            avatar={voiceInfo?.avatar}
            name={voiceInfo?.name}
            handleCloseDialog={handleCloseDialog}
            status={voiceInfo?.status}
            frameImageUrl={getFrameVoiceUrl(voiceInfo)}
          />
          <DescriptionVoice />
          <TagsVoice />
          <StyledInfoDetail>
            {!disabledInteract && <AudioSample />}
            <Box
              className="voice-info-wrapper"
              onClick={() => setIsEditVoiceInfo(true)}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Typography
                  component="p"
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('voiceInformation')}
                </Typography>
                {voice?.status === VOICE_STATUS.PUBLIC &&
                  voice?.subStatus === SUB_STATUS.REJECTED && (
                    <>
                      <StatusDetail subStatus={voiceInfo?.subStatus} />
                      <InfoRoundedIcon
                        sx={{ fontSize: '16px', color: COLOR.textRejected }}
                      />
                    </>
                  )}
                {voice?.status === VOICE_STATUS.PUBLIC &&
                  voice?.subStatus === SUB_STATUS.WAITING_FOR_APPROVAL && (
                    <>
                      <StatusDetail subStatus={voiceInfo?.subStatus} />
                    </>
                  )}
              </div>
              <KeyboardArrowRight className="icon" />
            </Box>
            {showBtnShare && (
              <Box
                className="voice-info-wrapper"
                onClick={handleRedirectVoiceProfile}
              >
                <Typography
                  component="p"
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {t('voiceProfilePage')}
                </Typography>
                <KeyboardArrowRight className="icon" />
              </Box>
            )}
            <StatusDetailPublishVoice
              setStepVoiceDetail={setStepVoiceDetail}
              voiceInfo={voiceInfo}
              onCancelPublishVoice={handleCancelPublishVoice}
              openPopupUnpublishVoice={() => setShowPopupUnpublishVoice(true)}
              onCancelUnpublishVoice={handleCancelUnpublishVoice}
              isLoadingCancelRequest={isLoadingCancelRequest}
              isLoadingUnpublishVoice={isLoadingUnpublishVoice}
              isLoadingCancelUnpublishVoice={isLoadingCancelUnpublishVoice}
            />
          </StyledInfoDetail>
          <ActionButtons />
        </StyledVoiceDetail>
      ) : (
        <InfoVoice />
      )}
    </>
  );

  const renderStep = () => {
    switch (stepVoiceDetail) {
      case STEP_VOICE_DETAIL.VOICE_DETAIL:
        return <DetailVoice />;
      case STEP_VOICE_DETAIL.FORM_SHARE_AND_EARN:
        return (
          <FormShareAndEarn
            voiceCloningInTTS={voiceCloningInTTS}
            onClose={handleCloseDialog}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    const keyRetentionDays =
      voiceInfo?.retentionDays === 365 ? 'yearNumber' : 'dayNumber';
    const valueRetentionDays =
      voiceInfo?.retentionDays === 365 ? 1 : voiceInfo?.retentionDays;

    return (
      <>
        <Typography
          variant="body-16-regular"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              t('unpublishVoiceDescription', { voiceName: voiceInfo?.name }),
              {
                allowedTags: sanitizeHtml.defaults.allowedTags,
                allowedAttributes: {
                  ...sanitizeHtml.defaults.allowedAttributes,
                  '*': ['style'],
                },
              },
            ),
          }}
        />

        <StyledDialogNote backgroundColor={TRANSPARENT_COLOR.alert}>
          <WarningRoundedIcon
            fontSize="inherit"
            className="note-icon-warning"
          />
          <Typography
            className="note-text"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('unpublishVoiceWarning', {
                  retentionDays: t(keyRetentionDays, {
                    value: valueRetentionDays,
                    days: valueRetentionDays,
                  }),
                }),
                {
                  allowedTags: sanitizeHtml.defaults.allowedTags,
                  allowedAttributes: {
                    ...sanitizeHtml.defaults.allowedAttributes,
                    '*': ['style'],
                  },
                },
              ),
            }}
          />
        </StyledDialogNote>
      </>
    );
  };

  const renderAction = () => (
    <>
      <Button
        variant="outlined"
        onClick={() => setShowPopupUnpublishVoice(false)}
        style={{
          textTransform: 'none',
          borderRadius: '8px',
          border: `1px solid ${COLOR.indigo[16]}`,
          backgroundColor: COLOR.white,
          height: '40px',
        }}
      >
        {t('cancel')}
      </Button>
      <Button
        variant="contained"
        style={{
          backgroundColor: COLOR.redPrimary,
          color: COLOR.white,
          textTransform: 'none',
          borderRadius: '8px',
          height: '40px',
        }}
        onClick={handleUnpublishVoice}
        disabled={isLoadingUnpublishVoice}
      >
        {t('unpublishVoice')}
      </Button>
    </>
  );

  return (
    <StyledNewDialogVoiceDetailDesktop
      newUiPublishVoice
      open={open}
      onClose={handleCloseDialog}
    >
      {renderStep()}
      <audio
        src={audioLink}
        ref={audioRef}
        style={{ display: 'none' }}
        onTimeUpdate={handleAudioTimeUpdate}
        onEnded={onAudioEnded}
      >
        <source src={audioLink} type="audio/ogg" />
        <track kind="captions" src="" label="Audio captions" />
      </audio>

      <CommonDialog
        title={t('unpublishVoiceLibrary')}
        contentComponent={renderContent()}
        actionComponent={renderAction()}
        open={showPopupUnpublishVoice}
        onClose={() => setShowPopupUnpublishVoice(false)}
      />
    </StyledNewDialogVoiceDetailDesktop>
  );
};

export default VoiceDetail;
